import { FeatureFlag } from '@caresend/types';
import { getEligibleTimeSlots, getArrayOfAllTimeSlots, convertCentsToDollar, getBestEligibleNurse } from '@caresend/utils';
import 'firebase/auth';
import '../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../database/firebaseConfig/init.js';
import '../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../components/ui/Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import '../../../../router/helpers/initRouter.js';
import { getStore } from '../../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { flagOn } from '../../variables/index.js';

const getOfficeToNursesMap = (timeSlot) => {
    var _a;
    const store = getStore();
    const { adminModeAllStaffingOffices = [] } = store.state.schedulingFlow;
    const initialOfficeToNursesMap = adminModeAllStaffingOffices.reduce((acc, office) => ({ ...acc, [office.id]: [] }), {});
    return Object.values((_a = timeSlot.availableNurses) !== null && _a !== void 0 ? _a : {}).reduce((officeToNursesMap, nurse) => {
        var _a;
        const { staffingOfficeID } = nurse;
        if (!staffingOfficeID || !officeToNursesMap[staffingOfficeID])
            return officeToNursesMap;
        if (staffingOfficeID)
            (_a = officeToNursesMap[staffingOfficeID]) === null || _a === void 0 ? void 0 : _a.push(nurse);
        return officeToNursesMap;
    }, initialOfficeToNursesMap);
};
/**
 * Return whether all supply instructions are present for a given draft waypoint.
 *
 * This is a similar function to the `getAreAllSupplyInstructionsPresentForDraftWaypoint`
 * getter from the `draftWaypoints` store. We need to do this because the supply
 * instructions are generally fetched and checked in the `draftWaypoints` store
 * prior to entering the scheduling flow. However, if a user enters the scheduling
 * flow directly from the visit list, the draft waypoint is not present yet in the
 * `draftWaypoints` store and will be fetched during the scheduling flow
 * initialization.
 */
const getAreAllSupplyInstructionsPresentForDraftWaypoint = (draftWaypointData) => {
    const { proceduresData } = draftWaypointData;
    const isSomeInstructionMissing = Object.values(proceduresData !== null && proceduresData !== void 0 ? proceduresData : {}).some((procedure) => !procedure.supplyInstructionConfigOptionsData);
    return !isSomeInstructionMissing;
};
const getOfficeFromProceduresData = (proceduresData) => { var _a, _b; return (_b = (_a = Object.values(proceduresData)[0]) === null || _a === void 0 ? void 0 : _a.orderData) === null || _b === void 0 ? void 0 : _b.officeData; };
const getPatientPays = (proceduresData) => { var _a, _b; return !!((_b = (_a = getOfficeFromProceduresData(proceduresData)) === null || _a === void 0 ? void 0 : _a.bookingPricing) === null || _b === void 0 ? void 0 : _b.paymentEnabled); };
const isSchedulingDryRun = (draftWaypoint) => {
    var _a;
    const isDryRunDueToFeatureFlag = flagOn(FeatureFlag.SCHEDULING_FLOW_DRY_RUN);
    const isDryRunDueToDebugProperty = !!((_a = draftWaypoint.debug) === null || _a === void 0 ? void 0 : _a.dryRun);
    return isDryRunDueToFeatureFlag || isDryRunDueToDebugProperty;
};
const getTextWithDryRun = (title, { store }) => {
    const isDryRun = store.state.schedulingFlow.draftWaypoint
        ? isSchedulingDryRun(store.state.schedulingFlow.draftWaypoint)
        : false;
    if (!isDryRun)
        return title;
    return title ? `${title} (dry run)` : 'Dry run enabled';
};
/**
 * Get the staffing info for each office per time slot.
 *
 * This is used to display the staffing info in the admin mode filters.
 *
 * @param timeSlots - The time slots to get the staffing info from.
 * @returns The staffing info per time slot.
 */
const getStaffingInfoPerTimeSlot = (timeSlots) => {
    const store = getStore();
    const { adminModeAllStaffingOffices = [] } = store.state.schedulingFlow;
    const staffingInfoByTimeSlot = {};
    const eligibleTimeSlots = getEligibleTimeSlots(timeSlots);
    const timeSlotArray = getArrayOfAllTimeSlots(eligibleTimeSlots);
    /**
     * Iterate over each time slot and get the staffing info (per office)
     * based off of the nurses on the timeslot that are associated with each
     * respecitive office.
     */
    timeSlotArray.forEach((timeSlot) => {
        if (timeSlot.disabled)
            return;
        const nursesByOffice = getOfficeToNursesMap(timeSlot);
        Object.entries(nursesByOffice).forEach(([officeID, nurses]) => {
            var _a, _b, _c, _d;
            const officeName = (_a = adminModeAllStaffingOffices.find((office) => office.id === officeID)) === null || _a === void 0 ? void 0 : _a.name;
            if (!officeName)
                return;
            if (nurses.length === 0)
                return;
            const startTime = timeSlot.startTime.toString();
            const sortedLaborCosts = nurses
                .map((nurse) => nurse.laborCost)
                .sort((a, b) => a - b);
            const cheapest = convertCentsToDollar((_b = sortedLaborCosts[0]) !== null && _b !== void 0 ? _b : 0);
            const mostExpensive = convertCentsToDollar((_c = sortedLaborCosts.at(-1)) !== null && _c !== void 0 ? _c : 0);
            const priceRange = sortedLaborCosts.length === 1
                ? [cheapest]
                : [cheapest, mostExpensive];
            if (!staffingInfoByTimeSlot[startTime])
                staffingInfoByTimeSlot[startTime] = [];
            (_d = staffingInfoByTimeSlot[startTime]) === null || _d === void 0 ? void 0 : _d.push({
                officeName,
                officeID,
                nurseCount: nurses.length,
                priceRange,
            });
        });
    });
    return staffingInfoByTimeSlot;
};
/**
 * Gets a time slot with some updated fields when confirming a mobile visit.
 *
 * Currently, this is used to update the time slot with the preferred offices
 * from the admin mode office filters and the best ranked nurse.
 */
const getUpdatedTimeSlot = (timeSlot) => {
    const store = getStore();
    const { adminMode, adminModeOfficeFilters = [], } = store.state.schedulingFlow;
    if (!adminMode)
        return timeSlot;
    const preferredOffices = adminModeOfficeFilters.map((office) => office.id);
    const bestNurse = getBestEligibleNurse(timeSlot.availableNurses, preferredOffices);
    const bestRankedNurseID = bestNurse === null || bestNurse === void 0 ? void 0 : bestNurse.nurseID;
    return {
        ...timeSlot,
        preferredOffices,
        bestRankedNurseID,
    };
};

export { getAreAllSupplyInstructionsPresentForDraftWaypoint, getOfficeFromProceduresData, getPatientPays, getStaffingInfoPerTimeSlot, getTextWithDryRun, getUpdatedTimeSlot, isSchedulingDryRun };
